import React from 'react'
import ReactDOM from 'react-dom/client'
import { registerSW } from 'virtual:pwa-register'
import { setWorkerUrl } from 'maplibre-gl'
// maplibre v6 ships its worker as a separate module; point it at Vite's bundled copy
import maplibreWorkerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url'
import 'maplibre-gl/dist/maplibre-gl.css'
import '@fontsource/plus-jakarta-sans/400.css'
import '@fontsource/plus-jakarta-sans/500.css'
import '@fontsource/plus-jakarta-sans/600.css'
import '@fontsource/plus-jakarta-sans/700.css'
import '@fontsource/plus-jakarta-sans/800.css'
import './styles.css'
import App from './App'
import { setupOfflineProtocol, requestPersistentStorage } from './lib/offline'
import { decodePlanHash } from './lib/share'
import { useAuth } from './state/auth'
import { useTours } from './state/tours'
import { usePlanner } from './state/planner'
import { useRecorder } from './state/recorder'

setWorkerUrl(maplibreWorkerUrl)
registerSW({ immediate: true })
setupOfflineProtocol()

let stableViewportHeight = Math.round(Math.max(window.visualViewport?.height ?? 0, window.innerHeight))
let stableViewportWidth = window.innerWidth

// Keep the app inside iOS's visible viewport and expose keyboard state to CSS.
function syncViewportHeight() {
  const h = Math.round(window.visualViewport?.height ?? window.innerHeight)
  const root = document.documentElement
  const active = document.activeElement
  const editing = active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement || active instanceof HTMLSelectElement

  if (Math.abs(window.innerWidth - stableViewportWidth) > 80) {
    stableViewportWidth = window.innerWidth
    stableViewportHeight = h
  } else if (h > stableViewportHeight) {
    stableViewportHeight = h
  }

  root.style.setProperty('--vvh', `${h}px`)
  root.classList.toggle('keyboard-open', editing && stableViewportHeight - h > 120)
}
syncViewportHeight()
window.visualViewport?.addEventListener('resize', syncViewportHeight)
window.addEventListener('resize', syncViewportHeight)
window.addEventListener('focusin', syncViewportHeight)
window.addEventListener('focusout', () => setTimeout(syncViewportHeight, 120))
window.addEventListener('orientationchange', () => {
  stableViewportHeight = 0
  setTimeout(syncViewportHeight, 250)
})
void requestPersistentStorage()

void useAuth
  .getState()
  .init()
  .then(() => useTours.getState().sync())
void useTours.getState().load()
void useRecorder.getState().restore()

// shared plan links: /#r=<sport>:<lng,lat;...>
const shared = decodePlanHash(location.hash)
if (shared) {
  usePlanner.getState().setWaypoints(
    shared.waypoints.map((w) => ({ lng: w[0], lat: w[1] })),
    shared.sport,
  )
  history.replaceState(null, '', '/')
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
)
